import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Menu, X, LogOut, Car, ChevronDown, Bell,
  LayoutDashboard, Crown, DollarSign, Users, AlertTriangle,
  FileText, Settings, Shield, CreditCard, Wallet, MessageSquare,
  Gavel, Image, Calendar, Clock, Rocket, Bug, UserCog,
  TrendingUp, ExternalLink, Wrench, Scale, Briefcase, Mail, Globe,
  Locate, Building, Handshake, ClipboardList, Gauge, KeyRound,
  Eye, ArrowRightLeft
} from 'lucide-react';

/**
 * PlatformOwnerAppShell - Unified navigation shell for all Platform Owner pages
 * 
 * Features:
 * - Grouped collapsible sidebar navigation (matches Host Dashboard behavior)
 * - Swipe-from-left gesture on mobile
 * - Consistent header with hamburger menu (NO back arrow)
 * - Auto-expand active section
 */

// Sidebar navigation configuration - mirrors PlatformOwnerDashboard sidebarGroups
const getSidebarGroups = () => [
  {
    title: 'Overview',
    icon: LayoutDashboard,
    items: [
      { id: 'dashboard', label: 'Dashboard', icon: Crown, href: '/platform-owner' },
      { id: 'support-inbox', label: 'Support Inbox', icon: MessageSquare, href: '/support-inbox' },
    ]
  },
  {
    title: 'Operations',
    icon: Briefcase,
    items: [
      { id: 'vehicle-mgmt', label: 'Vehicle Management', icon: Car, href: '/platform-owner/vehicles' },
      { id: 'users', label: 'Users', icon: Users, href: '/platform-owner', tab: 'users' },
      { id: 'claims', label: 'Claims', icon: Gavel, href: '/admin/claims' },
      { id: 'bookings', label: 'Bookings', icon: ClipboardList, href: '/platform-owner/bookings' },
      { id: 'recovery', label: 'Recovery Mode', icon: Locate, href: '/platform-owner/recovery' },
      { id: 'evidence', label: 'Evidence Viewer', icon: Image, href: '/platform-owner', tab: 'evidence' },
      { id: 'rego', label: 'Registration Expiry', icon: Calendar, href: '/platform-owner', tab: 'rego' },
      { id: 'overdue', label: 'Overdue Trips', icon: Clock, href: '/platform-owner', tab: 'overdue' },
      { id: 'disputes', label: 'Disputes', icon: AlertTriangle, href: '/platform-owner', tab: 'disputes' },
    ]
  },
  {
    title: 'Financial',
    icon: DollarSign,
    items: [
      { id: 'financial', label: 'Financial Enforcement', icon: TrendingUp, href: '/financial-enforcement' },
      { id: 'host-financials', label: 'Host Financials', icon: Building, href: '/admin/host-financials' },
      { id: 'charge-caps', label: 'Charge Caps', icon: Gauge, href: '/platform-owner/charge-caps' },
      { id: 'payment-auths', label: 'Payment Authorizations', icon: KeyRound, href: '/platform-owner/payment-authorizations' },
      { id: 'payouts', label: 'Payouts', icon: Wallet, href: '/platform-owner/payouts' },
      { id: 'stripe', label: 'Payment Dashboard', icon: CreditCard, href: '#stripe', external: true },
    ]
  },
  {
    title: 'Governance',
    icon: Scale,
    items: [
      { id: 'legal', label: 'Legal Pages', icon: Shield, href: '/platform-owner', tab: 'legal' },
      { id: 'audit', label: 'Audit Logs', icon: FileText, href: '/platform-owner', tab: 'audit' },
      { id: 'admins', label: 'Admin Management', icon: UserCog, href: '/platform-owner', tab: 'admins' },
      { id: 'host-agreements', label: 'Host Agreements', icon: Handshake, href: '/admin/agreements' },
      { id: 'email-centre', label: 'Email Centre', icon: Mail, href: '/platform-owner/email-centre' },
      { id: 'settings', label: 'Platform Settings', icon: Settings, href: '/platform-owner', tab: 'settings' },
      { id: 'social-links', label: 'Social Links', icon: Globe, href: '/platform-owner/social-links' },
      { id: 'security', label: 'Security', icon: Shield, href: '/platform-owner/security' },
      { id: 'security-activity', label: 'Security Activity', icon: Eye, href: '/platform-owner/security-activity' },
      { id: 'transfer-ownership', label: 'Transfer Ownership', icon: ArrowRightLeft, href: '/platform-owner/transfer-ownership' },
    ]
  },
  {
    title: 'Launch / Diagnostics',
    icon: Wrench,
    items: [
      { id: 'launch', label: 'Launch Preparation', icon: Rocket, href: '/platform-owner', tab: 'launch' },
      { id: 'debug', label: 'Platform Command Centre', icon: Bug, href: '/dev-debug' },
    ]
  },
];

// Sidebar Group Component
const SidebarGroup = ({ group, activeId, onItemClick, expandedGroup, onToggleGroup }) => {
  const isExpanded = expandedGroup === group.title;
  const hasActiveItem = group.items.some(item => item.id === activeId);
  const GroupIcon = group.icon;
  
  return (
    <div className="mb-1">
      {/* Group Header */}
      <button
        onClick={() => onToggleGroup(group.title)}
        className={`
          w-full flex items-center justify-between px-3 py-2.5 rounded-lg
          transition-all duration-200 text-xs font-semibold uppercase tracking-wider
          ${hasActiveItem 
            ? 'text-purple-300 bg-purple-500/10' 
            : 'text-white/50 hover:text-white/70 hover:bg-white/5'
          }
        `}
        data-testid={`po-group-${group.title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <div className="flex items-center gap-2">
          {GroupIcon && <GroupIcon className="w-4 h-4" />}
          <span>{group.title}</span>
        </div>
        <ChevronDown 
          className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} 
        />
      </button>
      
      {/* Group Items */}
      <div 
        className={`
          overflow-hidden transition-all duration-200 ease-out
          ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}
        `}
      >
        <div className="pl-2 pt-1 space-y-0.5">
          {group.items.map((item) => {
            const ItemIcon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => onItemClick(item)}
                className={`
                  w-full flex items-center gap-3 px-3 py-2 rounded-lg
                  transition-all duration-150 text-sm
                  ${activeId === item.id 
                    ? 'bg-purple-600/50 text-white' 
                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                  }
                `}
                data-testid={`po-nav-${item.id}`}
              >
                {ItemIcon && <ItemIcon className="w-4 h-4 flex-shrink-0" />}
                <span className="font-medium truncate">{item.label}</span>
                {item.external && (
                  <ExternalLink className="w-3 h-3 ml-auto text-white/40" />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export const PlatformOwnerAppShell = ({ 
  children, 
  title = 'Platform Owner',
  activeId = null,
  headerBadge = null,
  headerActions = null
}) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  
  // Swipe gesture support
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const sidebarRef = useRef(null);

  // Determine active ID from current route if not provided
  const currentActiveId = activeId || getActiveIdFromPath(location.pathname);
  const sidebarGroups = getSidebarGroups();

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-expand the group containing the active tab
  useEffect(() => {
    if (currentActiveId) {
      const groupWithActiveItem = sidebarGroups.find(group => 
        group.items.some(item => item.id === currentActiveId)
      );
      if (groupWithActiveItem) {
        setExpandedGroup(groupWithActiveItem.title);
      }
    }
  }, [currentActiveId]);

  // Close sidebar on route change
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when sidebar is open
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [sidebarOpen]);

  // Swipe gesture handlers
  useEffect(() => {
    const handleTouchStart = (e) => {
      touchStartX.current = e.touches[0].clientX;
      touchStartY.current = e.touches[0].clientY;
    };

    const handleTouchMove = (e) => {
      if (!touchStartX.current || !touchStartY.current) return;
      
      const touchEndX = e.touches[0].clientX;
      const touchEndY = e.touches[0].clientY;
      
      const deltaX = touchEndX - touchStartX.current;
      const deltaY = touchEndY - touchStartY.current;
      
      // Only trigger if horizontal swipe is dominant
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
        // Swipe right from left edge to open sidebar
        if (deltaX > 0 && touchStartX.current < 30 && !sidebarOpen) {
          setSidebarOpen(true);
          touchStartX.current = null;
          touchStartY.current = null;
        }
        // Swipe left to close sidebar
        if (deltaX < 0 && sidebarOpen) {
          setSidebarOpen(false);
          touchStartX.current = null;
          touchStartY.current = null;
        }
      }
    };

    const handleTouchEnd = () => {
      touchStartX.current = null;
      touchStartY.current = null;
    };

    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [sidebarOpen]);

  const handleNavClick = (item) => {
    // Handle Payment Dashboard external link
    if (item.id === 'stripe') {
      // Open Airwallex Dashboard in new tab
      window.open('https://www.airwallex.com/app', '_blank');
      return;
    }
    
    if (item.href) {
      navigate(item.href);
    }
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const handleToggleGroup = (groupTitle) => {
    setExpandedGroup(prev => prev === groupTitle ? null : groupTitle);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900 text-white">
      {/* Mobile Header - NO back arrow, only hamburger */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-black/80 backdrop-blur-xl border-b border-white/10 px-4 py-3 safe-area-inset-top">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => setSidebarOpen(true)} 
              className="text-white hover:bg-white/10"
              data-testid="po-mobile-menu-btn"
            >
              <Menu className="w-6 h-6" />
            </Button>
            <div className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-yellow-400" />
              <h1 className="font-heading font-semibold text-lg truncate">
                {title}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {headerBadge}
            {headerActions}
          </div>
        </div>
      </header>

      {/* Sidebar Backdrop */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
          data-testid="po-sidebar-backdrop"
        />
      )}

      {/* Sidebar */}
      <aside 
        ref={sidebarRef}
        className={`
          fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw]
          bg-black/95 backdrop-blur-xl border-r border-white/10
          transform transition-transform duration-300 ease-out
          lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        data-testid="po-sidebar"
      >
        <div className="h-full flex flex-col">
          {/* Sidebar Header */}
          <div className="p-4 lg:p-5 border-b border-white/10 flex items-center justify-between">
            <Link 
              to="/" 
              className="flex items-center gap-3 hover:opacity-80 transition-opacity"
              data-testid="po-sidebar-logo"
            >
              <div className="w-10 h-10 bg-[#004B8D] rounded-xl flex items-center justify-center flex-shrink-0">
                <Car className="w-6 h-6 text-white" />
              </div>
              <div className="min-w-0">
                <h1 className="font-heading font-bold text-base text-white">HireHub</h1>
                <p className="text-xs text-white/60">Platform Owner</p>
              </div>
            </Link>
            <Button 
              variant="ghost" 
              size="icon" 
              className="lg:hidden text-white hover:bg-white/10 flex-shrink-0" 
              onClick={() => setSidebarOpen(false)}
              data-testid="po-close-sidebar-btn"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* User Info */}
          {user && (
            <div className="px-4 py-3 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center text-sm text-white font-medium flex-shrink-0">
                  {user.full_name?.charAt(0) || user.email?.charAt(0) || 'P'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{user.full_name}</p>
                  <p className="text-[11px] text-white/50 truncate">{user.email}</p>
                </div>
                <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white border-0 text-[10px] px-1.5">
                  Owner
                </Badge>
              </div>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 p-3 overflow-y-auto scrollbar-thin scrollbar-thumb-white/10">
            <div className="space-y-1">
              {sidebarGroups.map((group) => (
                <SidebarGroup
                  key={group.title}
                  group={group}
                  activeId={currentActiveId}
                  onItemClick={handleNavClick}
                  expandedGroup={expandedGroup}
                  onToggleGroup={handleToggleGroup}
                />
              ))}
            </div>
          </nav>

          {/* Sidebar Footer */}
          <div className="p-3 border-t border-white/10">
            <Button 
              variant="ghost" 
              className="w-full justify-start gap-3 text-red-400 hover:text-red-300 hover:bg-red-500/10 h-10"
              onClick={handleLogout}
              data-testid="po-logout-btn"
            >
              <LogOut className="w-4 h-4" />
              <span className="text-sm">Sign Out</span>
            </Button>
          </div>
        </div>
      </aside>

      {/* Desktop Header */}
      <header className="hidden lg:flex fixed top-0 left-72 right-0 z-30 bg-black/50 backdrop-blur-xl border-b border-white/10 px-6 py-4 items-center justify-between">
        <div className="flex items-center gap-3">
          <Crown className="w-5 h-5 text-yellow-400" />
          <h1 className="font-heading text-xl font-semibold">{title}</h1>
          {headerBadge}
        </div>
        <div className="flex items-center gap-3">
          {headerActions}
        </div>
      </header>

      {/* Main Content */}
      <div className="lg:ml-72 pt-14 lg:pt-[73px] min-h-screen">
        <main className="p-4 sm:p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

// Helper function to determine active ID from pathname
function getActiveIdFromPath(pathname) {
  const pathMap = {
    '/platform-owner': 'dashboard',
    '/platform-owner/vehicles': 'vehicle-mgmt',
    '/platform-owner/payouts': 'payouts',
    '/platform-owner/social-links': 'social-links',
    '/platform-owner/email-centre': 'email-centre',
    '/platform-owner/charge-caps': 'charge-caps',
    '/platform-owner/payment-authorizations': 'payment-auths',
    '/platform-owner/bookings': 'bookings',
    '/platform-owner/recovery': 'recovery',
    '/financial-enforcement': 'financial',
    '/admin/claims': 'claims',
    '/admin/host-financials': 'host-financials',
    '/admin/agreements': 'host-agreements',
    '/support-inbox': 'support-inbox',
    '/dev-debug': 'debug',
  };
  
  // Check exact matches first
  if (pathMap[pathname]) {
    return pathMap[pathname];
  }
  
  // Check prefix matches
  if (pathname.startsWith('/platform-owner')) return 'dashboard';
  if (pathname.startsWith('/admin')) return 'dashboard';
  
  return 'dashboard';
}

export default PlatformOwnerAppShell;
